import { useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import RuleIcon from '@mui/icons-material/Rule';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  CircularProgress,
  IconButton,
  Typography,
} from '@mui/material';
import { Formik, Form, Field } from 'formik';
import { DataTable, PageHeader, Column, StatusChip } from '@/components/shared';
import TrioButton from '@/trio-ui/Button';
import { FxCategorizationRule, FxVendor, FxCategory } from '@/types/api/finance';

type Props = {
  rules: FxCategorizationRule[];
  vendors: FxVendor[];
  categories: FxCategory[];
  isLoading: boolean;
  showForm: boolean;
  editingRule: FxCategorizationRule | null;
  onNewRule: () => void;
  onEdit: (rule: FxCategorizationRule) => void;
  onDelete: (rule: FxCategorizationRule) => void;
  onSubmit: (data: any) => void;
  onCloseForm: () => void;
  isSubmitting: boolean;
  isLearning: boolean;
  learnResult: string | null;
  onLearnAndOptimize: () => void;
};

const sourceStatusMap = {
  ai_learned: { label: 'AI', color: '#054AD9', variant: 'filled' as const },
  user: { label: 'User', color: '#71717A', variant: 'outlined' as const },
};

const matchTypeStatusMap = {
  contains: { label: 'Contains', color: '#054AD9', variant: 'outlined' as const },
  exact: { label: 'Exact', color: '#2AD062', variant: 'outlined' as const },
  regex: { label: 'Regex', color: '#FFD166', variant: 'outlined' as const },
};

function getAccuracyColor(accuracy: number): string {
  if (accuracy >= 80) return '#2AD062';
  if (accuracy >= 50) return '#FFD166';
  return '#FF5E54';
}

const FinanceXRulesComponent = function ({
  rules,
  vendors,
  categories,
  isLoading,
  showForm,
  editingRule,
  onNewRule,
  onEdit,
  onDelete,
  onSubmit,
  onCloseForm,
  isSubmitting,
  isLearning,
  learnResult,
  onLearnAndOptimize,
}: Props): JSX.Element {
  const [confirmDelete, setConfirmDelete] = useState<FxCategorizationRule | null>(null);

  const columns: Column<FxCategorizationRule>[] = [
    {
      id: 'priority',
      label: 'Priority',
      width: '80px',
      render: (rule) => <span style={{ fontWeight: 600, fontSize: 13 }}>{rule.priority}</span>,
    },
    {
      id: 'name',
      label: 'Name',
      render: (rule) => <span style={{ fontWeight: 500, fontSize: 13 }}>{rule.name || '—'}</span>,
    },
    {
      id: 'matchType',
      label: 'Type',
      width: '100px',
      render: (rule) => (
        <StatusChip status={rule.matchType} statusMap={matchTypeStatusMap} size="small" />
      ),
    },
    {
      id: 'pattern',
      label: 'Pattern',
      render: (rule) => (
        <Typography
          variant="body2"
          sx={{
            fontFamily: 'monospace',
            fontSize: 12,
            backgroundColor: '#F4F4F5',
            px: 1,
            py: 0.5,
            borderRadius: '4px',
            display: 'inline-block',
          }}
        >
          {rule.matchPattern}
        </Typography>
      ),
    },
    {
      id: 'vendor',
      label: 'Vendor',
      render: (rule) => (
        <span style={{ fontSize: 13 }}>{rule.vendor?.displayName || rule.vendor?.name || '—'}</span>
      ),
    },
    {
      id: 'category',
      label: 'Category',
      render: (rule) =>
        rule.category ? (
          <Chip
            label={rule.category.name}
            size="small"
            sx={{
              fontSize: 11,
              height: 22,
              backgroundColor: `${rule.category.color}20`,
              color: rule.category.color ?? '#6B7280',
            }}
          />
        ) : (
          <span style={{ color: '#C5C5C7' }}>—</span>
        ),
    },
    {
      id: 'source',
      label: 'Source',
      width: '80px',
      render: (rule) => (
        <StatusChip status={rule.source} statusMap={sourceStatusMap} size="small" />
      ),
    },
    {
      id: 'hits',
      label: 'Hits',
      align: 'right',
      width: '60px',
      render: (rule) => <span style={{ color: '#71717A', fontSize: 13 }}>{rule.hitCount}</span>,
    },
    {
      id: 'accuracy',
      label: 'Accuracy',
      align: 'center',
      width: '90px',
      render: (rule) => {
        const total = rule.hitCount + rule.overrideCount;
        if (total === 0) return <span style={{ color: '#C5C5C7', fontSize: 13 }}>—</span>;
        const accuracy = Math.round((rule.hitCount / total) * 100);
        return (
          <Chip
            label={`${accuracy}%`}
            size="small"
            sx={{
              fontSize: 11,
              height: 22,
              fontWeight: 500,
              backgroundColor: `${getAccuracyColor(accuracy)}20`,
              color: getAccuracyColor(accuracy),
            }}
          />
        );
      },
    },
    {
      id: 'actions',
      label: '',
      align: 'right',
      width: '80px',
      render: (rule) => (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onEdit(rule);
            }}
          >
            <EditIcon sx={{ fontSize: 18 }} />
          </IconButton>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setConfirmDelete(rule);
            }}
            sx={{ color: '#FF5E54' }}
          >
            <DeleteIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ padding: '40px' }}>
      <PageHeader
        title="Categorization Rules"
        actions={
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TrioButton
              variantColor="blue"
              size="small"
              onClick={onLearnAndOptimize}
              disabled={isLearning}
            >
              {isLearning ? (
                <CircularProgress size={16} sx={{ mr: 0.5 }} />
              ) : (
                <AutoFixHighIcon sx={{ fontSize: 18, mr: 0.5 }} />
              )}
              {isLearning ? 'Learning...' : 'Learn & Optimize'}
            </TrioButton>
            <TrioButton variantColor="blue" size="small" onClick={onNewRule}>
              <AddIcon sx={{ fontSize: 18, mr: 0.5 }} /> Add Rule
            </TrioButton>
          </Box>
        }
      />

      {learnResult && (
        <Alert
          severity="info"
          sx={{ mb: 2, borderRadius: '8px' }}
          onClose={() => {}}
        >
          {learnResult}
        </Alert>
      )}

      <DataTable<FxCategorizationRule>
        columns={columns}
        data={rules}
        rowKey={(r) => r.id}
        isLoading={isLoading}
        searchPlaceholder="Search rules..."
        searchFields={(r) => `${r.name ?? ''} ${r.matchPattern} ${r.vendor?.name ?? ''}`}
        emptyMessage="No rules yet"
        emptyIcon={RuleIcon}
      />

      <Dialog open={showForm} onClose={onCloseForm} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>{editingRule ? 'Edit Rule' : 'Add Rule'}</DialogTitle>
        <Formik
          initialValues={{
            name: editingRule?.name ?? '',
            matchType: editingRule?.matchType ?? 'contains',
            matchPattern: editingRule?.matchPattern ?? '',
            vendorId: editingRule?.vendorId ?? '',
            categoryId: editingRule?.categoryId ?? '',
            priority: editingRule?.priority ?? 0,
          }}
          enableReinitialize
          onSubmit={(values) => {
            onSubmit({
              name: values.name || undefined,
              matchType: values.matchType,
              matchPattern: values.matchPattern,
              vendorId: values.vendorId || undefined,
              categoryId: values.categoryId || undefined,
              priority: Number(values.priority),
            });
          }}
        >
          {({ values, handleChange }) => (
            <Form>
              <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                <Field
                  as={TextField}
                  name="name"
                  label="Rule Name"
                  fullWidth
                  size="small"
                  placeholder="e.g. Match AWS charges"
                  value={values.name}
                  onChange={handleChange}
                />
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Field
                    as={TextField}
                    name="matchType"
                    label="Match Type"
                    select
                    size="small"
                    sx={{ flex: 1 }}
                    value={values.matchType}
                    onChange={handleChange}
                  >
                    <MenuItem value="contains">Contains</MenuItem>
                    <MenuItem value="exact">Exact</MenuItem>
                    <MenuItem value="regex">Regex</MenuItem>
                  </Field>
                  <Field
                    as={TextField}
                    name="priority"
                    label="Priority"
                    type="number"
                    size="small"
                    sx={{ width: 100 }}
                    value={values.priority}
                    onChange={handleChange}
                  />
                </Box>
                <Field
                  as={TextField}
                  name="matchPattern"
                  label="Pattern"
                  required
                  fullWidth
                  size="small"
                  placeholder="e.g. AMZN WEB SERVICES"
                  helperText="Text to match against bank transaction descriptions"
                  value={values.matchPattern}
                  onChange={handleChange}
                />
                <Field
                  as={TextField}
                  name="vendorId"
                  label="Assign to Vendor"
                  select
                  fullWidth
                  size="small"
                  value={values.vendorId}
                  onChange={handleChange}
                >
                  <MenuItem value="">None</MenuItem>
                  {vendors.map((v) => (
                    <MenuItem key={v.id} value={v.id}>
                      {v.displayName || v.name}
                    </MenuItem>
                  ))}
                </Field>
                <Field
                  as={TextField}
                  name="categoryId"
                  label="Assign to Category"
                  select
                  fullWidth
                  size="small"
                  value={values.categoryId}
                  onChange={handleChange}
                >
                  <MenuItem value="">None</MenuItem>
                  {categories.map((cat) => (
                    <MenuItem key={cat.id} value={cat.id}>
                      {cat.name}
                    </MenuItem>
                  ))}
                </Field>
              </DialogContent>
              <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onCloseForm} sx={{ textTransform: 'none' }}>
                  Cancel
                </Button>
                <TrioButton
                  type="submit"
                  variantColor="blue"
                  size="small"
                  disabled={!values.matchPattern || isSubmitting}
                >
                  {(() => {
                    if (isSubmitting) return <CircularProgress size={20} />;
                    return editingRule ? 'Update' : 'Create';
                  })()}
                </TrioButton>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)}>
        <DialogTitle>Delete Rule?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Are you sure you want to delete the rule &quot;{confirmDelete?.name || confirmDelete?.matchPattern}&quot;? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            sx={{ borderRadius: '999px', textTransform: 'none' }}
            onClick={() => {
              if (confirmDelete) onDelete(confirmDelete);
              setConfirmDelete(null);
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FinanceXRulesComponent;
